/**
 * InviteClaimPage — 访客领取邀请码页面
 * 路由: /invite/:token
 * 公开页面，无需登录
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Copy, Check, Camera, Loader2 } from 'lucide-react';
import * as C from '../constants';
import { getLogos } from '../utils/branding';
import { copyTextToClipboard, randomUUID } from '../utils/browserCompat'; // http 站点没有 crypto.randomUUID / navigator.clipboard

// 生成设备指纹：屏幕分辨率+色深+语言+时区+平台+UA → hash
// 用于设备识别的降级方案（当 localStorage 被清除时仍可匹配同一设备）
function generateFingerprint() {
  const raw = [
    window.screen.width, window.screen.height, window.screen.colorDepth,
    navigator.language, Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.platform, navigator.userAgent
  ].join('|');
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) - hash + raw.charCodeAt(i)) | 0;
  }
  return 'fp_' + Math.abs(hash).toString(36);
}

// 获取或生成 localStorage UUID — 设备识别的主标识
// 首次访问生成唯一 ID 并持久存储，后续访问带上，确保同设备返回同一邀请码
function getLocalToken() {
  const KEY = 'cineart_invite_device';
  let token = localStorage.getItem(KEY);
  if (!token) {
    token = 'lt_' + randomUUID(); // ⚠️ 别换回 crypto.randomUUID：它是 [SecureContext]，http 下整页白屏
    localStorage.setItem(KEY, token);
  }
  return token;
}

export default function InviteClaimPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ok | stopped | invalid | no_codes | error
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [durationDays, setDurationDays] = useState(null);
  const [branding, setBranding] = useState({ logos: [], logo: '', appName: 'Cineart Canvas' });
  const claimLogos = getLogos(branding);
  const cardRef = useRef(null);

  // 加载品牌配置
  useEffect(() => {
    fetch(`${C.API_BASE_URL}/api/distribution/branding`)
      .then(r => r.json())
      .then(setBranding)
      .catch(() => {});
  }, []);

  // 领取邀请码
  const claim = useCallback(async () => {
    try {
      const resp = await fetch(`${C.API_BASE_URL}/api/distribution/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          device_fingerprint: generateFingerprint(),
          local_token: getLocalToken(),
        }),
      });
      const data = await resp.json();
      if (data.status === 'ok') {
        setCode(data.code);
        setDurationDays(data.duration_days);
        setStatus('ok');
      } else {
        setStatus(data.status || 'error');
      }
    } catch {
      setStatus('error');
    }
  }, [token]);

  useEffect(() => { claim(); }, [claim]);

  // 复制
  const handleCopy = async () => {
    // 回退实现（textarea + execCommand）已抽到 utils/browserCompat
    if (await copyTextToClipboard(code)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // 截图保存
  const handleScreenshot = async () => {
    const el = cardRef.current;
    if (!el) return;
    try {
      // 动态导入 html2canvas（轻量级截图库）
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(el, {
        backgroundColor: '#0a0a0f',
        scale: 2,
      });
      const link = document.createElement('a');
      link.download = `邀请码_${code}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch {
      alert('截图功能需要网络支持，请手动截屏保存');
    }
  };

  return (
    <div style={styles.page}>
      <div ref={cardRef} style={styles.card}>
        {/* Logo + 名称 */}
        <div style={styles.logoSection}>
          {claimLogos.length > 0 ? (
            <div style={styles.logoRow}>
              {claimLogos.map((url, index) => (
                <img
                  key={`${url}-${index}`}
                  // 云存储上的是绝对地址，本地上传的是 /logos/xxx 这种相对路径
                  src={/^https?:\/\//.test(url) ? url : `${C.API_BASE_URL}${url}`}
                  alt=""
                  style={styles.logo}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              ))}
            </div>
          ) : (
            <div style={styles.logoFallback}>
              <svg width="48" height="48" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="lg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
                <circle cx="50" cy="50" r="45" fill="url(#lg)" opacity="0.15" />
                <text x="50" y="62" textAnchor="middle" fill="url(#lg)" fontSize="36" fontWeight="700">C</text>
              </svg>
            </div>
          )}
          <div style={styles.appName}>{branding.appName}</div>
        </div>

        {/* 欢迎加入 */}
        <div style={styles.welcome}>欢迎加入</div>

        {/* 内容区 */}
        {status === 'loading' && (
          <div style={styles.statusBox}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#8b5cf6' }} />
            <p style={styles.statusText}>正在获取邀请码…</p>
          </div>
        )}

        {status === 'ok' && (
          <>
            <div style={styles.codeBox}>
              <span style={styles.codeText}>{code}</span>
            </div>
            <p style={styles.hint}>
              {durationDays ? `📅 ${durationDays}天体验账户 · ` : ''}请保存此验证码用于注册
            </p>
            <div style={styles.actions}>
              <button onClick={handleCopy} style={styles.btn}>
                {copied ? <Check size={18} /> : <Copy size={18} />}
                <span>{copied ? '已复制' : '复制'}</span>
              </button>
              <button onClick={handleScreenshot} style={{ ...styles.btn, ...styles.btnSecondary }}>
                <Camera size={18} />
                <span>截图保存</span>
              </button>
            </div>
          </>
        )}

        {status === 'stopped' && (
          <div style={styles.statusBox}>
            <div style={styles.stoppedIcon}>⏸</div>
            <p style={styles.statusTitle}>放号阶段已结束</p>
            <p style={styles.statusText}>请关注后续通知</p>
          </div>
        )}

        {status === 'invalid' && (
          <div style={styles.statusBox}>
            <div style={styles.stoppedIcon}>❌</div>
            <p style={styles.statusTitle}>链接无效</p>
            <p style={styles.statusText}>请确认链接是否正确</p>
          </div>
        )}

        {status === 'no_codes' && (
          <div style={styles.statusBox}>
            <div style={styles.stoppedIcon}>📭</div>
            <p style={styles.statusTitle}>邀请码已发完</p>
            <p style={styles.statusText}>请联系管理员</p>
          </div>
        )}

        {status === 'error' && (
          <div style={styles.statusBox}>
            <div style={styles.stoppedIcon}>⚠️</div>
            <p style={styles.statusTitle}>网络错误</p>
            <p style={styles.statusText}>请检查网络后刷新重试</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1025 50%, #0a0a0f 100%)',
    padding: 20,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    width: '100%', maxWidth: 360,
    background: 'rgba(20, 20, 30, 0.9)',
    border: '1px solid rgba(139, 92, 246, 0.2)',
    borderRadius: 24,
    padding: '40px 32px',
    textAlign: 'center',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(139,92,246,0.1)',
  },
  logoSection: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    marginBottom: 24,
  },
  logoRow: {
    display: 'flex', alignItems: 'center', gap: 2, // 多个 Logo 并排，间距 2px
  },
  logo: {
    width: 56, height: 56, objectFit: 'contain', borderRadius: 12,
  },
  logoFallback: {
    width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  appName: {
    fontSize: 20, fontWeight: 700, letterSpacing: 1,
    background: 'linear-gradient(135deg, #c4b5fd, #67e8f9)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  welcome: {
    fontSize: 28, fontWeight: 300, color: 'rgba(255,255,255,0.9)',
    marginBottom: 32, letterSpacing: 6,
  },
  codeBox: {
    background: 'rgba(139, 92, 246, 0.08)',
    border: '2px solid rgba(139, 92, 246, 0.3)',
    borderRadius: 16, padding: '20px 24px',
    marginBottom: 12,
  },
  codeText: {
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    fontSize: 28, fontWeight: 700, letterSpacing: 4,
    color: '#a78bfa',
  },
  hint: {
    fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 24,
  },
  actions: {
    display: 'flex', gap: 12,
  },
  btn: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '14px 0', borderRadius: 12,
    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
    border: 'none', color: '#fff', fontSize: 15, fontWeight: 500,
    cursor: 'pointer', transition: 'opacity 0.2s',
  },
  btnSecondary: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
  },
  statusBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    padding: '20px 0',
  },
  stoppedIcon: {
    fontSize: 40, marginBottom: 4,
  },
  statusTitle: {
    fontSize: 18, fontWeight: 600, color: 'rgba(255,255,255,0.85)', margin: 0,
  },
  statusText: {
    fontSize: 14, color: 'rgba(255,255,255,0.4)', margin: 0,
  },
};
